import React from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary, DataCard, formatCHF } from '../regureStyles';
import useNewOfferFunctions from './useNewOfferFunctions';
import { useTenantProducts, productLabel } from '../../utils/productCatalog';
import PgmaOfferForm from './PgmaOfferForm';
import RcProOfferForm from './RcProOfferForm';
import PgmOfferForm from './PgmOfferForm';
import BrokerSelect from './BrokerSelect';
import MotorOfferFields from './MotorOfferFields';

// LOBs with a dedicated extended form (not the legacy single-input drawer).
const EXTENDED_LOBS = new Set(['PGMA', 'RC_PRO', 'PGM']);

// ─── Field Component (must be outside component to avoid unmount/remount on every render) ─────
const FormField = ({ label, children, labelStyle }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const NewOfferPanel = React.memo(function NewOfferPanel({ onClose, onCreated, reviseFrom = null, editDraft = null }) {
  const { t } = useTranslation();
  // Products the tenant actually sells. Unconfigured tenants get the full
  // platform list, so this narrows nothing until a tenant opts in.
  const products = useTenantProducts();
  // In revise mode the product is fixed: a revision of an RC Pro quote is
  // still an RC Pro quote (different product = different rate table and
  // template, i.e. a new quote, not a revision). The backend enforces the
  // same rule.
  // Editing a draft locks the product for the same reason revising does:
  // a different product is a different rate table and a different template,
  // i.e. a new quote rather than an edit of this one. The backend refuses
  // it either way.
  const lockedLob = reviseFrom?.lineOfBusiness || editDraft?.lineOfBusiness || null;
  const {
    focusedRef,
    currency,
    client,
    setClient,
    broker,
    setBroker,
    lob,
    setLob,
    age,
    setAge,
    salary,
    setSalary,
    waiting,
    setWaiting,
    riskType,
    setRiskType,
    sumInsured,
    setSumInsured,
    deductible,
    setDeductible,
    docCount,
    setDocCount,
    calculated,
    setCalculated,
    motor,
    setMotorField,
    manualPremium,
    setPremiumManually,
    saving,
    error,
    calcPremium,
    handleCreate,
    inputStyle,
    labelStyle,
  } = useNewOfferFunctions({ onCreated, onClose });

  // A money label names its currency, or names none at all. Before
  // tenantConfig has hydrated (and for a tenant that has configured no
  // currency) `currency` is empty, and "Annual Salary ()" is worse than
  // "Annual Salary" — the same rule resolveCurrency states for figures.
  const ccyLabel = (withCurrency, plain) =>
    (currency ? t(withCurrency, { currency }) : t(plain));

  // Locked product wins over local state so the correct extended form
  // mounts on the very first render of a revision, before any effect runs.
  const effectiveLob = lockedLob || lob;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.3)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        backdropFilter: 'blur(2px)',
      }}
      // Deliberately NOT closing on a backdrop click. This panel holds an
      // in-progress quote with no auto-persistence, and a stray click on the
      // dimmed area behind it used to discard everything the user had entered —
      // reported as "it takes me out of the quote". Closing is now only via the
      // explicit X or Cancel button, which are intentional actions. Standard
      // disableBackdropClick behaviour for a data-entry form.
    >
      <div
        style={{ width: (effectiveLob === 'RC_PRO' || effectiveLob === 'PGM') ? 580 : (effectiveLob === 'PGMA' || effectiveLob === 'MOTOR') ? 520 : 440, height: '100%', background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.15)', overflowY: 'auto', padding: 28 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>
            {reviseFrom
              ? t('offers.newOffer.reviseTitle')
              : editDraft
                ? t('offers.newOffer.editDraftTitle', { defaultValue: 'Edit draft' })
                : t('offers.newOffer.title')}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={20} />
          </button>
        </div>

        {reviseFrom && (
          <div style={{ padding: '10px 14px', background: 'rgba(128,0,32,0.04)', border: `1px solid ${S.burgundy}30`, borderRadius: 8, fontSize: 12, color: S.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
            {t('offers.newOffer.reviseNotice', { offerNumber: reviseFrom.offerNumber || reviseFrom.id })}
          </div>
        )}

        {editDraft && (
          <div style={{ padding: '10px 14px', background: 'rgba(128,0,32,0.04)', border: `1px solid ${S.burgundy}30`, borderRadius: 8, fontSize: 12, color: S.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>
            {t('offers.newOffer.editDraftNotice', {
              offerNumber: editDraft.offerNumber || editDraft.id,
              defaultValue: 'Editing draft {{offerNumber}}. Saving updates this draft — it keeps its number and stays a single entry in the list.',
            })}
          </div>
        )}

        {!EXTENDED_LOBS.has(effectiveLob) && (
          <FormField labelStyle={labelStyle} label={t('offers.newOffer.clientName')}>
            <input
              value={client}
              onChange={e => setClient(e.target.value)}
              onFocus={e => (focusedRef.current = e.target)}
              placeholder={t('offers.newOffer.clientNamePh')}
              style={inputStyle}
            />
          </FormField>
        )}
        <FormField labelStyle={labelStyle} label={t('offers.newOffer.lineOfBusiness')}>
          <select
            value={effectiveLob}
            onChange={e => {
              setLob(e.target.value);
              setCalculated(null);
            }}
            onFocus={e => (focusedRef.current = e.target)}
            disabled={Boolean(lockedLob)}
            style={{ ...inputStyle, cursor: lockedLob ? 'not-allowed' : 'pointer', background: lockedLob ? S.bgHover : '#fff' }}
          >
            <option value=''>{t('offers.newOffer.selectProduct')}</option>
            {products.map(code => (
              <option key={code} value={code}>
                {productLabel(t, code, null, 'long')}
              </option>
            ))}
          </select>
        </FormField>

        {effectiveLob === 'PGMA'   && <PgmaOfferForm  onClose={onClose} onCreated={onCreated} reviseFrom={reviseFrom} editDraft={editDraft} />}
        {effectiveLob === 'RC_PRO' && <RcProOfferForm onClose={onClose} onCreated={onCreated} reviseFrom={reviseFrom} editDraft={editDraft} />}
        {effectiveLob === 'PGM'    && <PgmOfferForm   onClose={onClose} onCreated={onCreated} reviseFrom={reviseFrom} editDraft={editDraft} />}

        {!EXTENDED_LOBS.has(effectiveLob) && effectiveLob && (
          <FormField labelStyle={labelStyle} label={t('offers.newOffer.broker', { defaultValue: 'Broker' })}>
            <BrokerSelect value={broker} onChange={setBroker} inputStyle={inputStyle} />
          </FormField>
        )}

        {!EXTENDED_LOBS.has(effectiveLob) && effectiveLob === 'IJM' && (
          <>
            <FormField labelStyle={labelStyle} label={t('offers.newOffer.ageBand')}>
              <select value={age} onChange={e => setAge(e.target.value)} onFocus={e => (focusedRef.current = e.target)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value='35'>{t('offers.newOffer.ageBand35')}</option>
                <option value='42'>{t('offers.newOffer.ageBand42')}</option>
                <option value='48'>{t('offers.newOffer.ageBand48')}</option>
                <option value='53'>{t('offers.newOffer.ageBand53')}</option>
                <option value='60'>{t('offers.newOffer.ageBand60')}</option>
              </select>
            </FormField>
            <FormField labelStyle={labelStyle} label={ccyLabel('offers.newOffer.annualSalary', 'offers.newOffer.annualSalaryPlain')}>
              <input type='number' value={salary} onChange={e => setSalary(e.target.value)} onFocus={e => (focusedRef.current = e.target)} style={inputStyle} />
            </FormField>
            <FormField labelStyle={labelStyle} label={t('offers.newOffer.waitingDays')}>
              <select
                value={waiting}
                onChange={e => setWaiting(e.target.value)}
                onFocus={e => (focusedRef.current = e.target)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value='30'>{t('offers.newOffer.days30')}</option>
                <option value='60'>{t('offers.newOffer.days60')}</option>
                <option value='90'>{t('offers.newOffer.days90')}</option>
              </select>
            </FormField>
            <FormField labelStyle={labelStyle} label={t('offers.newOffer.riskType')}>
              <select
                value={riskType}
                onChange={e => setRiskType(e.target.value)}
                onFocus={e => (focusedRef.current = e.target)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value='illness'>{t('offers.newOffer.illnessOnly')}</option>
                <option value='accident'>{t('offers.newOffer.illnessAccident')}</option>
              </select>
            </FormField>
            <FormField labelStyle={labelStyle} label={t('offers.newOffer.docsCovered')}>
              <input type='number' value={docCount} onChange={e => setDocCount(e.target.value)} onFocus={e => (focusedRef.current = e.target)} style={inputStyle} />
            </FormField>
          </>
        )}
        {effectiveLob === 'E&O' && (
          <>
            <FormField labelStyle={labelStyle} label={ccyLabel('offers.newOffer.sumInsuredCur', 'offers.newOffer.sumInsuredPlain')}>
              <select
                value={sumInsured}
                onChange={e => setSumInsured(e.target.value)}
                onFocus={e => (focusedRef.current = e.target)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {['250000', '500000', '1000000', '2000000', '5000000'].map(s => (
                  <option key={s} value={s}>
                    {formatCHF(Number(s))}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField labelStyle={labelStyle} label={ccyLabel('offers.newOffer.deductibleCur', 'offers.newOffer.deductiblePlain')}>
              <select
                value={deductible}
                onChange={e => setDeductible(e.target.value)}
                onFocus={e => (focusedRef.current = e.target)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {['2000', '5000', '10000', '20000'].map(d => (
                  <option key={d} value={d}>
                    {formatCHF(Number(d))}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField labelStyle={labelStyle} label={t('offers.newOffer.insuredDocs')}>
              <input type='number' value={docCount} onChange={e => setDocCount(e.target.value)} onFocus={e => (focusedRef.current = e.target)} style={inputStyle} />
            </FormField>
          </>
        )}
        {effectiveLob === 'PJ' && (
          <FormField labelStyle={labelStyle} label={t('offers.newOffer.docsCovered')}>
            <input type='number' value={docCount} onChange={e => setDocCount(e.target.value)} onFocus={e => (focusedRef.current = e.target)} style={inputStyle} />
          </FormField>
        )}
        {effectiveLob === 'MOTOR' && (
          <MotorOfferFields
            motor={motor}
            setMotorField={setMotorField}
            currency={currency}
            focusedRef={focusedRef}
            inputStyle={inputStyle}
            labelStyle={labelStyle}
          />
        )}

        {effectiveLob && !EXTENDED_LOBS.has(effectiveLob) && (
          <>
            {/* MOTOR carries no approved tariff on this platform, so the
                premium is stated by the underwriter rather than computed.
                A "Calculate" button over an engine that does not exist
                would be a claim the product cannot back. */}
            {effectiveLob === 'MOTOR' ? (
              <div style={{ marginTop: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${S.borderLight}` }}>
                  {t('offers.newOffer.motor.premiumSection')}
                </div>
                <FormField labelStyle={labelStyle} label={ccyLabel('offers.newOffer.motor.premium', 'offers.newOffer.motor.premiumPlain')}>
                  <input
                    type='number'
                    min={0}
                    step='0.001'
                    value={manualPremium}
                    onChange={e => setPremiumManually(e.target.value)}
                    onFocus={e => (focusedRef.current = e.target)}
                    style={inputStyle}
                  />
                </FormField>
              </div>
            ) : (
            <button
              onClick={calcPremium}
              style={{
                width: '100%',
                padding: '12px',
                background: S.bgCard,
                border: `1px solid ${S.borderLight}`,
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                color: S.textPrimary,
                cursor: 'pointer',
                marginBottom: 16,
              }}
            >
              {t('offers.newOffer.calculatePremium')}
            </button>
            )}

            {calculated !== null && (
              <DataCard style={{ marginBottom: 20, borderColor: S.burgundy + '30', background: 'rgba(128,0,32,0.02)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  {effectiveLob === 'MOTOR'
                    ? t('offers.newOffer.motor.premiumQuoted')
                    : t('offers.newOffer.calculatedPremium')}
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, color: S.burgundy, marginTop: 4 }}>{formatCHF(calculated)}</div>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{t('offers.newOffer.exclTaxes')}</div>
              </DataCard>
            )}

            {error && (
              <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>
                {t('offers.newOffer.cancel')}
              </BtnSecondary>
              <BtnPrimary onClick={handleCreate} style={{ flex: 1, justifyContent: 'center' }} disabled={!effectiveLob || calculated === null || saving}>
                {saving ? t('offers.newOffer.saving') : t('offers.newOffer.createQuote')}
              </BtnPrimary>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default NewOfferPanel;
